import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useNavigate } from "react-router-dom";

import {
  getKpis,
  getLeaderboard,
  getTrends,
  type LeaderboardMetric,
  type TrendPeriod,
} from "@/api/dashboard";
import { formatNumber } from "@/lib/utils";

interface CardProps {
  label: string;
  value: string;
  hint?: string;
  testid: string;
  tone?: "neutral" | "positive" | "warning" | "danger";
}

function KpiCard({ label, value, hint, testid, tone = "neutral" }: CardProps) {
  const toneCls =
    tone === "positive"
      ? "text-emerald-400"
      : tone === "warning"
        ? "text-amber-400"
        : tone === "danger"
          ? "text-rose-400"
          : "text-foreground";
  return (
    <div className="card flex flex-col gap-1" data-testid={testid}>
      <span className="text-xs text-muted-200">{label}</span>
      <span className={`text-3xl font-semibold ${toneCls}`}>{value}</span>
      {hint && <span className="text-xs text-muted-200">{hint}</span>}
    </div>
  );
}

export function DashboardPage() {
  const nav = useNavigate();
  const [period, setPeriod] = useState<TrendPeriod>("7d");
  const [metric, setMetric] = useState<LeaderboardMetric>("hits");

  const kpisQ = useQuery({
    queryKey: ["kpis"],
    queryFn: getKpis,
    refetchInterval: 30_000,
  });
  const trendsQ = useQuery({
    queryKey: ["dashboard-trends", period],
    queryFn: () => getTrends(period),
    refetchInterval: 60_000,
  });
  const leaderboardQ = useQuery({
    queryKey: ["dashboard-leaderboard", { metric, period }],
    queryFn: () => getLeaderboard({ metric, period, limit: 10 }),
    refetchInterval: 60_000,
  });

  const data = kpisQ.data;
  const trends = trendsQ.data;
  const leaderboard = leaderboardQ.data;

  return (
    <div className="p-6 space-y-6" data-testid="page-dashboard">
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Dashboard</h1>
          <p className="text-sm text-muted-200">
            Resumen ejecutivo en tiempo real.
          </p>
        </div>
        <PeriodToggle value={period} onChange={setPeriod} />
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard
          label="Unidades activas"
          value={data ? `${data.activeUnits}/${data.totalUnits}` : "—"}
          testid="kpi-units"
        />
        <KpiCard
          label="Lecturas hoy"
          value={data ? formatNumber(data.readingsToday) : "—"}
          testid="kpi-readings"
        />
        <KpiCard
          label="Hits hoy"
          value={data ? formatNumber(data.hitsToday) : "—"}
          hint={data ? `${data.pendingHits} pendientes` : undefined}
          testid="kpi-hits"
          tone={(data?.hitsToday ?? 0) > 0 ? "danger" : "neutral"}
        />
        <KpiCard
          label="Tasa de detección"
          value={data ? `${data.detectionRatePct.toFixed(2)}%` : "—"}
          testid="kpi-detection"
        />
      </div>

      <section className="card">
        <header className="mb-3">
          <h2 className="text-sm font-medium">Lecturas por hora (24 h)</h2>
        </header>
        <div className="h-72">
          {kpisQ.isLoading ? (
            <div className="text-muted-200 text-sm">Cargando…</div>
          ) : (
            <ResponsiveContainer>
              <BarChart data={data?.byHour24h ?? []}>
                <CartesianGrid stroke="#2a2f3a" strokeDasharray="3 3" />
                <XAxis
                  dataKey="bucket"
                  tickFormatter={(v) =>
                    new Date(v).toLocaleTimeString("es-MX", {
                      hour: "2-digit",
                    })
                  }
                  stroke="#9CA3AF"
                />
                <YAxis stroke="#9CA3AF" />
                <Tooltip
                  contentStyle={{
                    background: "#171a22",
                    border: "1px solid #2a2f3a",
                  }}
                  labelStyle={{ color: "#A8B0BD" }}
                />
                <Bar dataKey="rfid" stackId="a" fill="#8B5CF6" />
                <Bar dataKey="lpr" stackId="a" fill="#3B82F6" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </section>

      <section className="card">
        <header className="mb-3 flex items-baseline justify-between">
          <h2 className="text-sm font-medium">
            Tendencias ({period})
          </h2>
        </header>
        <div className="h-72">
          {trendsQ.isLoading ? (
            <div className="text-muted-200 text-sm">Cargando…</div>
          ) : (
            <ResponsiveContainer>
              <AreaChart data={trends?.points ?? []}>
                <defs>
                  <linearGradient id="gReadings" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.6} />
                    <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gHits" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#F43F5E" stopOpacity={0.6} />
                    <stop offset="95%" stopColor="#F43F5E" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#2a2f3a" strokeDasharray="3 3" />
                <XAxis dataKey="day" stroke="#9CA3AF" />
                <YAxis stroke="#9CA3AF" />
                <Tooltip
                  contentStyle={{
                    background: "#171a22",
                    border: "1px solid #2a2f3a",
                  }}
                  labelStyle={{ color: "#A8B0BD" }}
                />
                <Area
                  type="monotone"
                  dataKey="readings"
                  stroke="#8B5CF6"
                  fill="url(#gReadings)"
                />
                <Area
                  type="monotone"
                  dataKey="hits"
                  stroke="#F43F5E"
                  fill="url(#gHits)"
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </section>

      <section className="card">
        <header className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-sm font-medium">Leaderboard</h2>
          <select
            className="select text-xs"
            value={metric}
            onChange={(e) =>
              setMetric(e.target.value as LeaderboardMetric)
            }
          >
            <option value="hits">Hits</option>
            <option value="readings">Lecturas</option>
            <option value="distancia_km">Distancia</option>
            <option value="anomalias_abiertas">Anomalías abiertas</option>
            <option value="tiempo_movimiento_seconds">
              Tiempo en movimiento
            </option>
          </select>
        </header>
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-xs uppercase text-muted-200 border-b border-border">
              <th className="px-2 py-1.5 text-left">#</th>
              <th className="px-2 py-1.5 text-left">Unidad</th>
              <th className="px-2 py-1.5 text-left">Placa</th>
              <th className="px-2 py-1.5 text-left">Gerencia</th>
              <th className="px-2 py-1.5 text-right">Valor</th>
            </tr>
          </thead>
          <tbody>
            {(leaderboard?.rows ?? []).map((r) => (
              <tr
                key={r.unitId}
                className="border-b border-border/40 hover:bg-surface2 cursor-pointer"
                onClick={() => nav(`/units/${r.unitId}`)}
                data-testid={`leaderboard-row-${r.unitId}`}
              >
                <td className="px-2 py-1.5">{r.rank}</td>
                <td className="px-2 py-1.5 font-medium">{r.alias}</td>
                <td className="px-2 py-1.5">{r.placa ?? "—"}</td>
                <td className="px-2 py-1.5">{r.gerencia ?? "—"}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">
                  {formatLeaderboardValue(metric, r.value)}
                </td>
              </tr>
            ))}
            {(leaderboard?.rows ?? []).length === 0 && !leaderboardQ.isLoading && (
              <tr>
                <td
                  colSpan={5}
                  className="px-2 py-4 text-center text-muted-200"
                >
                  Sin datos para los filtros actuales.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function PeriodToggle({
  value,
  onChange,
}: {
  value: TrendPeriod;
  onChange: (next: TrendPeriod) => void;
}) {
  const periods: TrendPeriod[] = ["7d", "30d", "90d"];
  return (
    <div className="inline-flex rounded-md border border-border overflow-hidden">
      {periods.map((p) => (
        <button
          key={p}
          className={
            "px-3 py-1 text-xs " +
            (value === p
              ? "bg-primary text-white"
              : "bg-surface2 text-muted-200 hover:text-foreground")
          }
          onClick={() => onChange(p)}
          data-testid={`period-${p}`}
        >
          {p}
        </button>
      ))}
    </div>
  );
}

function formatLeaderboardValue(
  metric: LeaderboardMetric,
  value: number
): string {
  if (metric === "distancia_km") return `${value.toFixed(1)} km`;
  if (metric === "tiempo_movimiento_seconds") {
    const h = Math.floor(value / 3600);
    const m = Math.floor((value % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  }
  return formatNumber(value);
}
